import React, {useMemo} from "react"
import {
    createDayOfMonthLabels,
    extractAvailableSeasons,
    generateCountByKey,
    getDayOfYearLabel,
    getSeason, getSeasonLabel,
    trimLabels
} from "../utils";
import {lightBlue} from "@mui/material/colors";
import HighchartsReact from "highcharts-react-official";
import {useTranslation} from "react-i18next";
import useHighChartLocaleOptions from "../useHighChartLocaleOptions";
import Highcharts, {chartLogoOptions, creditsOptions, exportingOptions} from "../highChartsBase";
import Toolbar, {useToolbar} from "./Toolbar";

const IncidentDailyCounts = ({data: originalData}) => {
    const {t} = useTranslation("graphs");
    const highChartLocaleOptions = useHighChartLocaleOptions();

    const height = 500;

    const data = useMemo(() => (originalData.map(({date, ...rest}) => {
            return {
                date,
                season: getSeason(date),
                dayOfYear: getDayOfYearLabel(date),
                ...rest
            }
        })
    ), [originalData]);
    const attribute = 'dayOfYear';

    const availableSeasons = useMemo( () => extractAvailableSeasons(data), [data] );
    const toolbar = useToolbar({availableSeasons});

    // Get the data filtered by the toolbar selections
    const filteredData = useMemo( () => (
        data.filter( item => {
            const itemSeason = item.season;
            return (toolbar.seasonFrom ? itemSeason >= toolbar.seasonFrom : true)  && (toolbar.seasonTo ? itemSeason <= toolbar.seasonTo : true)
        })
    ), [data, toolbar.seasonFrom, toolbar.seasonTo]);

    const seasonStat = generateCountByKey(filteredData, [attribute], 'dayOfYear');
    const labels = createDayOfMonthLabels(true);
    const trimmedLabels = trimLabels(labels, {...filteredData, ...seasonStat});

    const subtitle = t('subtitle.seasonFrom', {seasonFrom: getSeasonLabel(toolbar.seasonFrom)}) + (toolbar.seasonTo ? t('subtitle.seasonTo', {seasonTo: getSeasonLabel(toolbar.seasonTo)}) : '');

    const options = {
        ...highChartLocaleOptions,
        exporting: {
            ...exportingOptions.exporting,
            filename: `incident_daily_counts`
        },
        title: {
            text: t('incidentDailyCounts.title')
        },
        subtitle: {
            text: subtitle
        },
        series: [
            {
                data: trimmedLabels.map(label => seasonStat[label] ?? null),
                type: 'column',
                name: t('incidentDailyCounts.numberOfIncidentsPerDay'),
                color: lightBlue[500],
                yAxis: 0,
            },
        ],
        xAxis: {
            categories: trimmedLabels
        },
        yAxis: [
            {
                title: {
                    text: t('incidentDailyCounts.numberOfIncidents')
                },
                allowDecimals: false,
            },
        ],
        tooltip: {
            shared: true,
            crosshairs: true
        },
        plotOptions: {
            series: {
                marker: {
                    enabled: true,
                    symbol: 'circle',
                },
                line: {
                    connectNulls: false // Do not connect null points
                },
                states: {
                    inactive: {
                        opacity: 1 // Prevent fading by keeping opacity at 1
                    }
                }
            },
            column: {
                dataLabels: {
                    enabled: true,
                    crop: false,
                    overflow: 'none',
                    formatter: function() {
                        return this.y;
                    }
                }
            }
        },
        chart: {
            zoomType: 'x', // Enables horizontal zooming; use 'y' or 'xy' for vertical or both directions
            height: height,
            ...chartLogoOptions,
        },
        ...creditsOptions,
    }

    return <>
        <Toolbar {...toolbar} />
        <HighchartsReact highcharts={Highcharts} options={options} />
    </>
}

export default IncidentDailyCounts